import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormField, Input, Modal, Select, Spinner } from '@vwfs-bronson/bronson-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { AuthTokens } from '../../auth';
import {
  Agent,
  ChatMessageItem,
  ChatSseEvent,
  FesConfig,
  Job,
  Team,
  connectChatSse,
  getAgents,
  getAllTeams,
  getChatMessages,
  getJobStatus,
  getJobs,
  sendChatMessage,
  startJob,
} from '../../fes';
import { ChatMessage, ToolCall, buildMessages } from './transcript';

const ACTIVE_JOB_STATUSES = new Set(['QUEUED', 'STARTING', 'RUNNING']);
const TERMINAL_JOB_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED', 'PURGED']);
// Per stage: team ids are not portable between them, so a remembered cons team would
// select nothing on dev.
function lastChatTeamKey(baseUrl: string): string {
  return `agent_backoffice_chat_team_id_${baseUrl}`;
}
const bottomPinThresholdPx = 80;

function readStoredTeamId(baseUrl: string): string {
  try {
    return window.localStorage.getItem(lastChatTeamKey(baseUrl)) || '';
  } catch {
    return '';
  }
}

function storeTeamId(baseUrl: string, teamId: string): void {
  try {
    if (teamId) {
      window.localStorage.setItem(lastChatTeamKey(baseUrl), teamId);
    } else {
      window.localStorage.removeItem(lastChatTeamKey(baseUrl));
    }
  } catch {
    // A blocked storage quota must not stop a chat from starting.
  }
}

function getTeamLabel(teams: Team[], teamId: string | undefined): string {
  if (!teamId) {
    return 'No team';
  }

  return teams.find(team => team.teamId === teamId)?.name || teamId;
}

function describeTeamAccess(team: Team | undefined, teamsError: string | undefined): string {
  if (teamsError) {
    return `Teams could not be loaded (${teamsError}). The chat starts without a team.`;
  }

  if (!team) {
    return 'The team decides which GitHub installation and repositories the agent can reach.';
  }

  if (team.installationId === undefined) {
    return 'This team has no GitHub App installation connected.';
  }

  const repositoryCount = team.allowedRepos?.length ?? 0;

  return `GitHub installation ${team.installationId}${repositoryCount ? ` - ${repositoryCount} repositories` : ''}`;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

function createRequestId(): string {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function scheduleFrame(callback: () => void): number {
  if (typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback);
  }

  return window.setTimeout(callback, 0);
}

function cancelFrame(frameId: number): void {
  if (typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(frameId);
    return;
  }

  window.clearTimeout(frameId);
}

function timeAgo(dateValue: string | undefined): string {
  if (!dateValue) {
    return '';
  }

  const timestamp = new Date(dateValue).getTime();

  if (!Number.isFinite(timestamp)) {
    return '';
  }

  const minutes = Math.floor((Date.now() - timestamp) / 60_000);

  if (minutes < 1) {
    return 'just now';
  }

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  if (days < 7) {
    return `${days}d ago`;
  }

  return new Date(dateValue).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getJobTitle(job: Job | undefined, fallbackRequestId: string): string {
  if (!job) {
    return fallbackRequestId ? fallbackRequestId.slice(0, 8) : 'No chat selected';
  }

  return job.issueId || job.initialPrompt || [job.githubOrg, job.repositoryName].filter(Boolean).join('/') || 'Chat';
}

function isConversationJob(job: Job): boolean {
  if (job.origin?.channel) {
    return job.origin.channel === 'chat';
  }

  // Older chat jobs predate origin metadata, so keep them for backwards
  // compatibility. New jobs carry an origin and can be separated reliably.
  return true;
}

function sanitizeStage(value: string): string {
  const trimmedValue = value.replace(/[\s.]+$/, '').trim();

  return trimmedValue ? `${trimmedValue}...` : 'Working...';
}

function parseTelemetryStage(source: string | undefined, message: string | undefined): string | null {
  if (!source || !message) {
    return null;
  }

  const normalizedSource = source.toLowerCase();

  if (normalizedSource.includes('stdout')) {
    try {
      const parsed = JSON.parse(message) as { type?: string };

      switch (parsed.type) {
        case 'thread.started':
          return 'Thinking...';
        case 'turn.started':
          return 'Thinking...';
        case 'turn.completed':
          return 'Generating response...';
        case 'turn.failed':
          return 'Turn failed...';
        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  if (normalizedSource.includes('stderr')) {
    return sanitizeStage(message);
  }

  return null;
}

function hasAgentMessageAfterLastUser(messages: ChatMessage[]): boolean {
  const lastUserIndex = messages.reduce((latestIndex, message, index) => (message.role === 'user' ? index : latestIndex), -1);

  return lastUserIndex >= 0 && messages.slice(lastUserIndex + 1).some(message => message.role === 'agent');
}

function renderContent(content: string) {
  const parts: Array<{ type: 'text' | 'code'; language?: string; value: string }> = [];
  const fencePattern = /```([\w.-]+)?\n([\s\S]*?)```/g;
  let cursor = 0;
  let match = fencePattern.exec(content);

  while (match) {
    if (match.index > cursor) {
      parts.push({ type: 'text', value: content.slice(cursor, match.index) });
    }

    parts.push({ type: 'code', language: match[1], value: match[2].replace(/\n$/, '') });
    cursor = match.index + match[0].length;
    match = fencePattern.exec(content);
  }

  if (cursor < content.length) {
    parts.push({ type: 'text', value: content.slice(cursor) });
  }

  if (parts.length === 0) {
    parts.push({ type: 'text', value: content });
  }

  return parts.map((part, index) =>
    part.type === 'code' ? (
      <div className="app-chat-code-block" key={`${part.type}-${index}`}>
        <div className="app-chat-code-header">{part.language || 'code'}</div>
        <pre>
          <code>{part.value}</code>
        </pre>
      </div>
    ) : (
      <p className="app-chat-message-text" key={`${part.type}-${index}`}>
        {part.value}
      </p>
    )
  );
}

function ToolCallRow({ call }: { call: ToolCall }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasOutput = call.output.trim().length > 0;
  const hasPreview = Boolean(call.preview && call.preview.trim());
  const hasDetails = hasOutput || hasPreview;
  const summary = call.preview || (call.status === 'running' ? 'running...' : call.status);

  return (
    <div className="app-chat-tool">
      <button
        type="button"
        className="app-chat-tool-summary"
        aria-expanded={hasDetails ? isOpen : undefined}
        disabled={!hasDetails}
        onClick={() => setIsOpen(currentValue => !currentValue)}
      >
        <span>{isOpen ? 'v' : '>'}</span>
        <strong>{call.tool}</strong>
        <span className="app-chat-tool-preview">{summary}</span>
      </button>
      {isOpen && hasPreview ? <p className="app-chat-tool-preview-full">{call.preview}</p> : null}
      {isOpen && hasOutput ? <pre className="app-chat-tool-output">{call.output}</pre> : null}
    </div>
  );
}

function ChatMessageBubble({
  content,
  createdAt,
  isThinking,
  isStreaming,
  reasoningFrames,
  role,
  thinkingStage,
  tools,
}: ChatMessage & { isThinking?: boolean; isStreaming?: boolean; thinkingStage?: string | null }) {
  const [isReasoningOpen, setIsReasoningOpen] = useState(false);
  const reasoningCount = reasoningFrames?.length || 0;

  return (
    <article className={`app-chat-message app-chat-message--${role}`}>
      <div className="app-chat-avatar" aria-hidden="true">
        {role === 'user' ? 'U' : 'A'}
      </div>
      <div className="app-chat-message-body">
        {isThinking && !content ? (
          <div className="app-chat-thinking" role="status">
            <span className="app-chat-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            {thinkingStage || 'Thinking...'}
          </div>
        ) : null}
        {reasoningCount > 0 ? (
          <div className="app-chat-reasoning">
            <button type="button" onClick={() => setIsReasoningOpen(currentValue => !currentValue)}>
              {isReasoningOpen ? 'Hide' : 'Show'} {reasoningCount} reasoning {reasoningCount === 1 ? 'step' : 'steps'}
            </button>
            {isReasoningOpen ? (
              <ol>
                {reasoningFrames?.map((frame, index) => (
                  <li key={`${frame}-${index}`}>{frame}</li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
        {tools && tools.length > 0 ? (
          <div className="app-chat-tools">
            {tools.map(tool => (
              <ToolCallRow call={tool} key={tool.itemId} />
            ))}
          </div>
        ) : null}
        {content ? renderContent(content) : null}
        {isStreaming && content ? <span className="app-chat-cursor" aria-hidden="true" /> : null}
        {createdAt ? <time className="app-chat-message-time">{new Date(createdAt).toLocaleString()}</time> : null}
      </div>
    </article>
  );
}

function JobStatusBanner({
  fesConfig,
  onReady,
  requestId,
  tokens,
}: {
  fesConfig: FesConfig;
  onReady: () => void;
  requestId: string;
  tokens: AuthTokens;
}) {
  const [status, setStatus] = useState('STARTING');
  const [dots, setDots] = useState('');

  useEffect(() => {
    let cancelled = false;
    let previousStatus: string | null = null;
    const startedAt = Date.now();
    const maxWaitMs = 120_000;

    const poll = async () => {
      while (!cancelled) {
        try {
          const response = await getJobStatus(fesConfig, tokens, requestId);

          if (cancelled) {
            return;
          }

          setStatus(response.status);

          if (response.status === 'RUNNING') {
            await delay(1000);

            if (!cancelled) {
              onReady();
            }

            return;
          }

          if (TERMINAL_JOB_STATUSES.has(response.status) && previousStatus !== null && previousStatus !== response.status) {
            return;
          }

          if (Date.now() - startedAt > maxWaitMs) {
            return;
          }

          previousStatus = response.status;
        } catch {
          // Retry while the dispatcher catches up.
        }

        await delay(3000);
      }
    };

    void poll();

    return () => {
      cancelled = true;
    };
  }, [fesConfig, onReady, requestId, tokens]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setDots(currentValue => (currentValue.length >= 3 ? '' : `${currentValue}.`)), 500);

    return () => window.clearInterval(intervalId);
  }, []);

  const label =
    status === 'RUNNING'
      ? 'Agent connected'
      : status === 'COMPLETED'
        ? `Resuming agent${dots}`
        : status === 'FAILED' || status === 'CANCELLED' || status === 'PURGED'
          ? `Job ${status.toLowerCase()}`
          : `Starting agent${dots}`;

  return <div className="app-chat-job-banner">{label}</div>;
}

function ChatPanel({
  activeJob,
  fesConfig,
  isNewThread,
  jobs,
  onJobChanged,
  onJobMonitored,
  requestId,
  teams,
  tokens,
}: {
  activeJob?: Job;
  fesConfig: FesConfig;
  isNewThread: boolean;
  jobs: Job[];
  onJobChanged: (job: Job) => void;
  onJobMonitored: () => void;
  requestId: string;
  teams: Team[];
  tokens: AuthTokens;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamContent, setStreamContent] = useState('');
  const [streamReasoningFrames, setStreamReasoningFrames] = useState<string[]>([]);
  const [streamTools, setStreamTools] = useState<ToolCall[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string>();
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingStage, setThinkingStage] = useState<string | null>(null);
  const [showJobMonitor, setShowJobMonitor] = useState(false);
  const [pendingResumeMessage, setPendingResumeMessage] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string>();
  const [lastKnownJobStatus, setLastKnownJobStatus] = useState<string | undefined>(activeJob?.status);
  const sseRef = useRef<{ close: () => void } | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const isPinnedToBottomRef = useRef(true);
  const cursorRef = useRef('0-0');
  const contentAccumulatorRef = useRef('');
  const reasoningFramesRef = useRef<string[]>([]);
  const toolsMapRef = useRef<Map<string, ToolCall>>(new Map());
  const toolsOrderRef = useRef<string[]>([]);
  const sawTextDeltaRef = useRef(false);
  const dirtyRef = useRef({ content: false, frames: false, tools: false });
  const rafIdRef = useRef<number | null>(null);
  const activeJobRef = useRef<Job | undefined>(activeJob);
  const onJobChangedRef = useRef(onJobChanged);
  const pendingUserContentRef = useRef<string | null>(null);

  useEffect(() => {
    activeJobRef.current = activeJob;
    onJobChangedRef.current = onJobChanged;
  }, [activeJob, onJobChanged]);

  const snapshotTools = useCallback((): ToolCall[] => {
    return toolsOrderRef.current
      .map(toolId => toolsMapRef.current.get(toolId))
      .filter((tool): tool is ToolCall => Boolean(tool))
      .map(tool => ({ ...tool }));
  }, []);

  const cancelScheduledFlush = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    dirtyRef.current = { content: false, frames: false, tools: false };
  }, []);

  const scheduleFlush = useCallback(() => {
    if (rafIdRef.current !== null) {
      return;
    }

    rafIdRef.current = scheduleFrame(() => {
      rafIdRef.current = null;
      const dirty = dirtyRef.current;

      if (dirty.content) {
        setStreamContent(contentAccumulatorRef.current);
      }

      if (dirty.frames) {
        setStreamReasoningFrames([...reasoningFramesRef.current]);
      }

      if (dirty.tools) {
        setStreamTools(snapshotTools());
      }

      dirtyRef.current = { content: false, frames: false, tools: false };
    });
  }, [snapshotTools]);

  const resetStreamState = useCallback(() => {
    contentAccumulatorRef.current = '';
    reasoningFramesRef.current = [];
    toolsMapRef.current = new Map();
    toolsOrderRef.current = [];
    sawTextDeltaRef.current = false;
    setStreamContent('');
    setStreamReasoningFrames([]);
    setStreamTools([]);
    setThinkingStage(null);
  }, []);

  const applyPolledMessages = useCallback(
    (items: ChatMessageItem[], latestAgentStreamId: string | undefined) => {
      const builtMessages = buildMessages(items || []);
      const pendingUserContent = pendingUserContentRef.current;

      if (
        pendingUserContent &&
        !builtMessages.some(message => message.role === 'user' && message.content.trim() === pendingUserContent)
      ) {
        return;
      }

      if (pendingUserContent) {
        pendingUserContentRef.current = null;
      }

      setMessages(currentMessages => {
        if (builtMessages.length === 0 && currentMessages.length > 0) {
          return currentMessages;
        }

        return builtMessages;
      });

      if (latestAgentStreamId) {
        cursorRef.current = latestAgentStreamId;
      }

      if (hasAgentMessageAfterLastUser(builtMessages)) {
        sseRef.current?.close();
        sseRef.current = null;
        cancelScheduledFlush();
        resetStreamState();
        setIsStreaming(false);
        setIsThinking(false);
        setThinkingStage(null);
      }
    },
    [cancelScheduledFlush, resetStreamState]
  );

  const refreshChatHistory = useCallback(() => {
    if (!requestId) {
      return;
    }

    getChatMessages(fesConfig, tokens, requestId)
      .then(response => {
        applyPolledMessages(response.items || [], response.cursor?.latestAgentStreamId);
      })
      .catch(() => {
        // The interval poll and SSE stream continue to handle transient history failures.
      });
  }, [applyPolledMessages, fesConfig, requestId, tokens]);

  const commitTurn = useCallback(() => {
    cancelScheduledFlush();

    const content = contentAccumulatorRef.current.trimEnd();
    const reasoningFrames = [...reasoningFramesRef.current];
    const tools = snapshotTools();

    if (content || reasoningFrames.length || tools.length) {
      setMessages(currentMessages => [
        ...currentMessages,
        {
          id: `agent-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          role: 'agent',
          content,
          reasoningFrames: reasoningFrames.length ? reasoningFrames : undefined,
          tools: tools.length ? tools : undefined,
          createdAt: new Date().toISOString(),
        },
      ]);
    }

    resetStreamState();
  }, [cancelScheduledFlush, resetStreamState, snapshotTools]);

  const startSse = useCallback(
    (after: string, showThinking = true) => {
      if (!requestId) {
        return;
      }

      if (sseRef.current) {
        return;
      }

      cancelScheduledFlush();
      resetStreamState();
      setIsStreaming(showThinking);
      setIsThinking(showThinking);
      setThinkingStage(showThinking ? 'Thinking...' : null);

      sseRef.current = connectChatSse(
        fesConfig,
        tokens,
        requestId,
        after,
        (event: ChatSseEvent) => {
          if (event.id) {
            cursorRef.current = event.id;
          }

          switch (event.type) {
            case 'text':
              setIsStreaming(true);
              setIsThinking(false);
              setThinkingStage(null);

              if (!sawTextDeltaRef.current) {
                contentAccumulatorRef.current += `${event.content || ''}\n`;
                dirtyRef.current.content = true;
                scheduleFlush();
              }
              break;
            case 'text_delta':
              setIsStreaming(true);
              setIsThinking(false);
              setThinkingStage(null);

              if (event.delta) {
                contentAccumulatorRef.current += event.delta;
                sawTextDeltaRef.current = true;
                dirtyRef.current.content = true;
                scheduleFlush();
              }
              break;
            case 'status': {
              const nextStage = event.message || event.content;
              if (nextStage?.trim()) {
                setThinkingStage(sanitizeStage(nextStage));
              }
              break;
            }
            case 'reasoning_delta':
              setIsStreaming(true);
              if (event.delta?.trim()) {
                reasoningFramesRef.current = [...reasoningFramesRef.current, event.delta.trim()];
                dirtyRef.current.frames = true;
                scheduleFlush();
              }
              break;
            case 'tool_call_start':
              setIsStreaming(true);
              if (event.itemId && event.tool && !toolsMapRef.current.has(event.itemId)) {
                toolsMapRef.current.set(event.itemId, {
                  itemId: event.itemId,
                  tool: event.tool,
                  preview: event.preview,
                  output: '',
                  status: 'running',
                });
                toolsOrderRef.current.push(event.itemId);
                dirtyRef.current.tools = true;
                scheduleFlush();
              }
              break;
            case 'tool_call_delta': {
              setIsStreaming(true);
              if (!event.itemId) {
                break;
              }

              const currentTool = toolsMapRef.current.get(event.itemId);

              if (currentTool) {
                currentTool.output += event.delta || '';
              } else if (event.tool) {
                toolsMapRef.current.set(event.itemId, {
                  itemId: event.itemId,
                  tool: event.tool,
                  output: event.delta || '',
                  status: 'running',
                });
                toolsOrderRef.current.push(event.itemId);
              }

              dirtyRef.current.tools = true;
              scheduleFlush();
              break;
            }
            case 'tool_call_end': {
              setIsStreaming(true);
              if (!event.itemId) {
                break;
              }

              const currentTool = toolsMapRef.current.get(event.itemId);

              if (currentTool) {
                currentTool.status = event.status === 'error' ? 'error' : 'ok';
                if (event.preview) {
                  currentTool.preview = event.preview;
                }
                dirtyRef.current.tools = true;
                scheduleFlush();
              }
              break;
            }
            case 'message_end':
              setIsThinking(false);
              break;
            case 'telemetry': {
              const nextStage = parseTelemetryStage(event.source, event.message);

              if (nextStage) {
                setThinkingStage(nextStage);
              }
              break;
            }
            case 'turn_complete':
              commitTurn();
              pendingUserContentRef.current = null;
              setIsStreaming(false);
              setIsThinking(false);
              setThinkingStage(null);
              break;
            case 'done':
              commitTurn();
              pendingUserContentRef.current = null;
              setIsStreaming(false);
              setIsThinking(false);
              setThinkingStage(null);
              sseRef.current = null;
              break;
            case 'error':
              cancelScheduledFlush();
              setMessages(currentMessages => [
                ...currentMessages,
                {
                  id: `error-${Date.now()}`,
                  role: 'agent',
                  content: `Error: ${event.message || 'Unknown error'}`,
                  createdAt: new Date().toISOString(),
                },
              ]);
              resetStreamState();
              pendingUserContentRef.current = null;
              setIsStreaming(false);
              setIsThinking(false);
              setThinkingStage(null);
              sseRef.current = null;
              break;
          }
        },
        () => {
          commitTurn();
          setIsStreaming(false);
          setIsThinking(false);
          setThinkingStage(null);
          sseRef.current = null;
        }
      );
    },
    [cancelScheduledFlush, commitTurn, fesConfig, requestId, resetStreamState, scheduleFlush, tokens]
  );

  const handleHistoryScroll = useCallback(() => {
    const history = historyRef.current;

    if (!history) {
      return;
    }

    const distanceFromBottom = history.scrollHeight - history.scrollTop - history.clientHeight;

    isPinnedToBottomRef.current = distanceFromBottom <= bottomPinThresholdPx;
  }, []);

  useEffect(() => {
    // Reading back through a conversation must not be interrupted: only follow new
    // output while the reader is still parked at the bottom of the history.
    if (!isPinnedToBottomRef.current) {
      return;
    }

    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, streamContent, streamReasoningFrames, streamTools, isThinking]);

  useEffect(() => {
    isPinnedToBottomRef.current = true;
    bottomRef.current?.scrollIntoView?.({ block: 'end' });
  }, [requestId]);

  useEffect(() => {
    if (isNewThread && requestId) {
      setShowJobMonitor(true);
    }
  }, [isNewThread, requestId]);

  useEffect(() => {
    setLastKnownJobStatus(activeJob?.status);
  }, [activeJob?.status]);

  useEffect(() => {
    if (!requestId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    sseRef.current?.close();
    sseRef.current = null;
    cancelScheduledFlush();
    resetStreamState();
    setIsStreaming(false);
    setIsThinking(false);
    setShowJobMonitor(false);
    setResumeError(undefined);
    setHistoryError(undefined);
    setIsHistoryLoading(true);

    const load = async () => {
      try {
        const response = await getChatMessages(fesConfig, tokens, requestId);

        if (cancelled) {
          return;
        }

        applyPolledMessages(response.items || [], response.cursor?.latestAgentStreamId || '0-0');

        const statusResponse = await getJobStatus(fesConfig, tokens, requestId).catch(() => null);

        if (!cancelled && statusResponse?.status) {
          setLastKnownJobStatus(statusResponse.status);
          onJobChangedRef.current({ ...(activeJobRef.current || { requestId }), requestId, status: statusResponse.status });
        }

      } catch (error) {
        if (!cancelled) {
          setHistoryError(error instanceof Error ? error.message : 'Unable to load chat history.');
        }
      } finally {
        if (!cancelled) {
          setIsHistoryLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      sseRef.current?.close();
      if (rafIdRef.current !== null) {
        cancelFrame(rafIdRef.current);
      }
    };
  }, [applyPolledMessages, cancelScheduledFlush, fesConfig, requestId, resetStreamState, tokens]);

  useEffect(() => {
    if (!requestId) {
      return;
    }

    const shouldPoll =
      isSending ||
      isStreaming ||
      isThinking ||
      showJobMonitor ||
      (lastKnownJobStatus ? ACTIVE_JOB_STATUSES.has(lastKnownJobStatus) : false);

    if (!shouldPoll) {
      return;
    }

    let cancelled = false;
    let isPolling = false;

    const poll = async () => {
      if (isPolling) {
        return;
      }

      isPolling = true;

      try {
        const [messagesResponse, statusResponse] = await Promise.all([
          getChatMessages(fesConfig, tokens, requestId),
          getJobStatus(fesConfig, tokens, requestId).catch(() => null),
        ]);

        if (cancelled) {
          return;
        }

        applyPolledMessages(messagesResponse.items || [], messagesResponse.cursor?.latestAgentStreamId);

        if (statusResponse?.status) {
          setLastKnownJobStatus(statusResponse.status);
          onJobChangedRef.current({ ...(activeJobRef.current || { requestId }), requestId, status: statusResponse.status });

          if (!ACTIVE_JOB_STATUSES.has(statusResponse.status) && !isSending) {
            setShowJobMonitor(false);
            setIsStreaming(false);
            setIsThinking(false);
            setThinkingStage(null);
          }
        }
      } catch {
        // SSE remains primary; polling is a best-effort fallback.
      } finally {
        isPolling = false;
      }
    };

    void poll();
    const intervalId = window.setInterval(() => {
      void poll();
    }, 2500);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    applyPolledMessages,
    fesConfig,
    isSending,
    isStreaming,
    isThinking,
    lastKnownJobStatus,
    requestId,
    showJobMonitor,
    tokens,
  ]);

  const handleJobReady = useCallback(() => {
    setShowJobMonitor(false);
    onJobMonitored();
    onJobChangedRef.current({ ...(activeJobRef.current || { requestId }), requestId, status: 'RUNNING' });

    if (pendingResumeMessage) {
      const message = pendingResumeMessage;
      setPendingResumeMessage(null);
      pendingUserContentRef.current = message;
      sendChatMessage(fesConfig, tokens, requestId, message)
        .then(() => {
          sseRef.current?.close();
          sseRef.current = null;
          startSse(cursorRef.current);
          refreshChatHistory();
        })
        .catch(error => {
          setMessages(currentMessages => [
            ...currentMessages,
            {
              id: `error-${Date.now()}`,
              role: 'agent',
              content: `Failed to send after resume: ${error instanceof Error ? error.message : 'Unknown error'}`,
              createdAt: new Date().toISOString(),
            },
          ]);
          pendingUserContentRef.current = null;
        });
      return;
    }

    startSse('0-0');
  }, [fesConfig, onJobMonitored, pendingResumeMessage, refreshChatHistory, requestId, startSse, tokens]);

  const handleStop = () => {
    sseRef.current?.close();
    sseRef.current = null;
    commitTurn();
    setIsStreaming(false);
    setIsThinking(false);
    setThinkingStage(null);
    void sendChatMessage(fesConfig, tokens, requestId, '/done').catch(() => undefined);
  };

  const handleSend = async () => {
    const content = draft.trim();

    if (!content || isSending || showJobMonitor) {
      return;
    }

    // Sending is an explicit request for the newest output, so follow the thread again.
    isPinnedToBottomRef.current = true;

    setDraft('');
    setIsSending(true);
    setResumeError(undefined);
    setMessages(currentMessages => [
      ...currentMessages,
      {
        id: `user-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      },
    ]);
    setIsThinking(true);
    setThinkingStage('Sending...');
    pendingUserContentRef.current = content;

    try {
      const statusResponse = await getJobStatus(fesConfig, tokens, requestId);
      setLastKnownJobStatus(statusResponse.status);

      if (!ACTIVE_JOB_STATUSES.has(statusResponse.status)) {
        const sourceJob = activeJob || jobs.find(job => job.requestId === requestId);

        if (!sourceJob?.agentId) {
          setResumeError('Cannot resume: original agent metadata was not found.');
          return;
        }

        setPendingResumeMessage(content);
        await startJob(fesConfig, tokens, { requestId, agentId: sourceJob.agentId }, { teamId: sourceJob.teamId });
        onJobChanged({ ...sourceJob, status: 'STARTING' });
        setLastKnownJobStatus('STARTING');
        setShowJobMonitor(true);
        return;
      }

      await sendChatMessage(fesConfig, tokens, requestId, content);

      sseRef.current?.close();
      sseRef.current = null;
      startSse(cursorRef.current);
      refreshChatHistory();
    } catch (error) {
      setMessages(currentMessages => [
        ...currentMessages,
        {
          id: `error-${Date.now()}`,
          role: 'agent',
          content: `Failed to send: ${error instanceof Error ? error.message : 'Unknown error'}`,
          createdAt: new Date().toISOString(),
        },
      ]);
      pendingUserContentRef.current = null;
      setIsThinking(false);
      setThinkingStage(null);
    } finally {
      setIsSending(false);
    }
  };

  if (!requestId) {
    return (
      <section className="app-chat-panel app-chat-panel--empty">
        <div className="app-chat-empty">
          <h2>No chat selected</h2>
          <p>Select a conversation, or use New Chat in the conversation panel.</p>
        </div>
      </section>
    );
  }

  const conversationTitle = getJobTitle(activeJob, requestId);

  return (
    <section className="app-chat-panel" aria-label="Chat conversation">
      <header className="app-chat-panel-header">
        <div>
          <h2 title={conversationTitle}>{conversationTitle}</h2>
          <p>{requestId}</p>
          <p className="app-chat-panel-team">Team: {getTeamLabel(teams, activeJob?.teamId)}</p>
        </div>
        <div className="app-chat-panel-actions">
          {isStreaming ? (
            <Button secondary icon="semantic-close" onClick={handleStop}>
              Stop
            </Button>
          ) : null}
        </div>
      </header>
      <div className="app-chat-history" onScroll={handleHistoryScroll} ref={historyRef}>
        {isHistoryLoading ? (
          <div className="app-detail-loading" role="status">
            <Spinner center>Loading chat...</Spinner>
          </div>
        ) : null}
        {historyError ? <div className="app-result app-team-error">{historyError}</div> : null}
        {!isHistoryLoading && !historyError && messages.length === 0 && !showJobMonitor && !isThinking ? (
          <div className="app-chat-empty app-chat-empty--compact">
            <h2>Send a message to start</h2>
          </div>
        ) : null}
        {messages.map(message => (
          <ChatMessageBubble {...message} key={message.id} />
        ))}
        {showJobMonitor ? (
          <JobStatusBanner fesConfig={fesConfig} onReady={handleJobReady} requestId={requestId} tokens={tokens} />
        ) : null}
        {resumeError ? <div className="app-chat-inline-error">{resumeError}</div> : null}
        {(isStreaming || isThinking) && (streamContent || streamReasoningFrames.length || streamTools.length || isThinking) ? (
          <ChatMessageBubble
            id="streaming-agent-message"
            role="agent"
            content={streamContent}
            createdAt=""
            reasoningFrames={streamReasoningFrames.length ? streamReasoningFrames : undefined}
            tools={streamTools.length ? streamTools : undefined}
            isStreaming={Boolean(streamContent)}
            isThinking={isThinking}
            thinkingStage={thinkingStage}
          />
        ) : null}
        {isSending && !isStreaming && !isThinking ? <div className="app-chat-sending">Sending...</div> : null}
        <div ref={bottomRef} />
      </div>
      <form
        className="app-chat-composer"
        onSubmit={event => {
          event.preventDefault();
          void handleSend();
        }}
      >
        <Input
          aria-label="Message"
          disabled={showJobMonitor}
          onChange={event => setDraft(event.target.value)}
          placeholder="Send a message..."
          value={draft}
        />
        <Button disabled={isSending || showJobMonitor || !draft.trim()} icon="semantic-forward" type="submit">
          Send
        </Button>
      </form>
    </section>
  );
}

function NewChatModal({
  fesConfig,
  isLoadingTeams,
  onClose,
  onCreated,
  open,
  teams,
  teamsError,
  tokens,
}: {
  fesConfig: FesConfig;
  isLoadingTeams: boolean;
  onClose: () => void;
  onCreated: (job: Job) => void;
  open: boolean;
  teams: Team[];
  teamsError?: string;
  tokens: AuthTokens;
}) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentId, setAgentId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string>();
  const selectedTeam = teams.find(team => team.teamId === teamId);
  const isTeamRequired = teams.length > 0;

  useEffect(() => {
    if (!open) {
      return;
    }

    setTeamId(currentTeamId => {
      if (currentTeamId && teams.some(team => team.teamId === currentTeamId)) {
        return currentTeamId;
      }

      const storedTeamId = readStoredTeamId(fesConfig.baseUrl);

      if (storedTeamId && teams.some(team => team.teamId === storedTeamId)) {
        return storedTeamId;
      }

      return teams.length === 1 ? teams[0].teamId : '';
    });
  }, [open, teams, fesConfig.baseUrl]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setError(undefined);
    setIsLoadingAgents(true);

    const load = async () => {
      try {
        const response = await getAgents(fesConfig, tokens);
        const loadedAgents = response.data || [];

        if (cancelled) {
          return;
        }

        setAgents(loadedAgents);
        setAgentId(currentAgentId => currentAgentId || loadedAgents[0]?.agentId || '');
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Unable to load agents.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAgents(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [fesConfig, open, tokens]);

  const handleClose = () => {
    if (!isStarting) {
      onClose();
    }
  };

  const handleCreate = async () => {
    if (isStarting) {
      return;
    }

    if (!agentId) {
      setError('Select an agent for this chat.');
      return;
    }

    if (isTeamRequired && !teamId) {
      setError('Select a team for this chat.');
      return;
    }

    const requestId = createRequestId();
    setIsStarting(true);
    setError(undefined);

    try {
      await startJob(fesConfig, tokens, { requestId, agentId }, { teamId });

      storeTeamId(fesConfig.baseUrl, teamId);

      const selectedAgent = agents.find(agent => agent.agentId === agentId);
      onCreated({
        requestId,
        agentId,
        status: 'STARTING',
        createdAt: new Date().toISOString(),
        initialPrompt: selectedAgent?.name,
        origin: { channel: 'chat', teamId: teamId || undefined },
        ...(teamId ? { teamId } : {}),
      });
      onClose();
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : 'Unable to start chat.');
    } finally {
      setIsStarting(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Modal
      buttonCancelText="Cancel"
      buttonConfirmText={isStarting ? 'Starting...' : 'Start chat'}
      buttonConfirmType="button"
      className="app-chat-new-modal"
      id="new-chat-modal"
      onCancel={handleClose}
      onClose={handleClose}
      onConfirm={handleCreate}
      shown
      title="New Chat"
    >
      <form
        className="app-create-team-form"
        onSubmit={event => {
          event.preventDefault();
          void handleCreate();
        }}
      >
        {isLoadingAgents || isLoadingTeams ? (
          <div className="app-detail-loading app-detail-loading--compact" role="status">
            <Spinner center>Loading agents and teams...</Spinner>
          </div>
        ) : null}
        <FormField labelForId="new-chat-team" labelText="Team" notion type="select">
          <Select
            disabled={isStarting || isLoadingTeams || teams.length === 0}
            id="new-chat-team"
            onChange={event => setTeamId(event.target.value)}
            value={teamId}
          >
            <Select.Item value="" disabled>
              Select a team
            </Select.Item>
            {teams.map(team => (
              <Select.Item key={team.teamId} value={team.teamId}>
                {team.name ? `${team.name} (${team.teamId})` : team.teamId}
              </Select.Item>
            ))}
          </Select>
        </FormField>
        <p className="app-chat-field-hint">{describeTeamAccess(selectedTeam, teamsError)}</p>
        <FormField labelForId="new-chat-agent" labelText="Agent" notion type="select">
          <Select
            disabled={isStarting || isLoadingAgents}
            id="new-chat-agent"
            onChange={event => setAgentId(event.target.value)}
            value={agentId}
          >
            <Select.Item value="" disabled>
              Select an agent
            </Select.Item>
            {agents.map(agent => (
              <Select.Item key={agent.agentId} value={agent.agentId}>
                {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
              </Select.Item>
            ))}
          </Select>
        </FormField>
        {error ? <div className="app-result app-team-error">{error}</div> : null}
      </form>
    </Modal>
  );
}

export function BackofficeChatView({ fesConfig, tokens }: { fesConfig: FesConfig; tokens: AuthTokens }) {
  const navigate = useNavigate();
  const location = useLocation();
  const chatMatch = matchPath('/chat/:requestId', location.pathname);
  const routeThreadId = chatMatch?.params.requestId ? decodeURIComponent(chatMatch.params.requestId) : '';
  const [selectedThreadId, setSelectedThreadId] = useState(routeThreadId);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState<string>();
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [newThreadId, setNewThreadId] = useState<string>();
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamsError, setTeamsError] = useState<string>();
  const activeThreadId = selectedThreadId;

  useEffect(() => {
    setSelectedThreadId(routeThreadId);
  }, [routeThreadId]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('new') !== '1') {
      return;
    }

    setIsNewChatModalOpen(true);
    navigate('/chat', { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingTeams(true);

    getAllTeams(fesConfig, tokens)
      .then(loadedTeams => {
        if (!cancelled) {
          setTeams(loadedTeams);
          setTeamsError(undefined);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setTeamsError(error instanceof Error ? error.message : 'Unable to load teams.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingTeams(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fesConfig, tokens]);

  const loadJobs = useCallback(async (options: { preserveCurrent?: boolean } = {}) => {
    setIsLoadingJobs(true);
    setJobsError(undefined);

    try {
      const response = await getJobs(fesConfig, tokens, { limit: 50, offset: 0 });
      const loadedJobs = (response.data || []).filter(isConversationJob);
      setJobs(currentJobs => {
        if (!options.preserveCurrent) {
          return loadedJobs.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
        }

        const jobsById = new Map(currentJobs.map(job => [job.requestId, job]));
        loadedJobs.forEach(job => {
          jobsById.set(job.requestId, { ...jobsById.get(job.requestId), ...job });
        });

        return Array.from(jobsById.values()).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      });
    } catch (error) {
      setJobsError(error instanceof Error ? error.message : 'Unable to load conversations.');
    } finally {
      setIsLoadingJobs(false);
    }
  }, [fesConfig, tokens]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const activeJob = useMemo(() => jobs.find(job => job.requestId === activeThreadId), [activeThreadId, jobs]);

  const handleSelectThread = (requestId: string) => {
    setSelectedThreadId(requestId);
    navigate(`/chat/${encodeURIComponent(requestId)}`);
  };

  const handleJobChanged = (changedJob: Job) => {
    setJobs(currentJobs => {
      const hasJob = currentJobs.some(job => job.requestId === changedJob.requestId);
      const nextJobs = hasJob
        ? currentJobs.map(job => (job.requestId === changedJob.requestId ? { ...job, ...changedJob } : job))
        : [changedJob, ...currentJobs];

      return nextJobs.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    });
  };

  const handleCreated = (job: Job) => {
    handleJobChanged(job);
    setNewThreadId(job.requestId);
    setSelectedThreadId(job.requestId);
    navigate(`/chat/${encodeURIComponent(job.requestId)}`);
    void loadJobs({ preserveCurrent: true });
  };

  return (
    <div className="app-chat">
      <aside className="app-chat-sidebar" aria-label="Conversations">
        <div className="app-chat-sidebar-header">
          <Button icon="semantic-plus" onClick={() => setIsNewChatModalOpen(true)}>
            New Chat
          </Button>
          <Button secondary icon="semantic-reload" disabled={isLoadingJobs} onClick={() => void loadJobs()}>
            Refresh
          </Button>
        </div>
        {jobsError ? <div className="app-chat-inline-error">{jobsError}</div> : null}
        {isLoadingJobs && jobs.length === 0 ? (
          <div className="app-detail-loading app-detail-loading--compact" role="status">
            <Spinner center>Loading conversations...</Spinner>
          </div>
        ) : null}
        {!isLoadingJobs && jobs.length === 0 ? <p className="app-empty">No conversations found.</p> : null}
        <div className="app-chat-thread-list">
          {jobs.map(job => (
            <button
              className={job.requestId === activeThreadId ? 'app-chat-thread app-chat-thread--active' : 'app-chat-thread'}
              key={job.requestId}
              onClick={() => handleSelectThread(job.requestId)}
              type="button"
            >
              <span className="app-chat-thread-title">{getJobTitle(job, job.requestId)}</span>
              <span className="app-chat-thread-meta">
                <span>{job.status || 'UNKNOWN'}</span>
                <span>{timeAgo(job.createdAt)}</span>
              </span>
              <span className="app-chat-thread-team">{getTeamLabel(teams, job.teamId)}</span>
              <span className="app-chat-thread-id">{job.requestId}</span>
            </button>
          ))}
        </div>
      </aside>
      <ChatPanel
        activeJob={activeJob}
        fesConfig={fesConfig}
        isNewThread={newThreadId === activeThreadId}
        jobs={jobs}
        key={activeThreadId || 'empty-chat'}
        onJobChanged={handleJobChanged}
        onJobMonitored={() => setNewThreadId(undefined)}
        requestId={activeThreadId}
        teams={teams}
        tokens={tokens}
      />
      <NewChatModal
        fesConfig={fesConfig}
        isLoadingTeams={isLoadingTeams}
        onClose={() => setIsNewChatModalOpen(false)}
        onCreated={handleCreated}
        open={isNewChatModalOpen}
        teams={teams}
        teamsError={teamsError}
        tokens={tokens}
      />
    </div>
  );
}
